import { api } from '../api/client';
import { API_PACKAGES_URL } from '../utils';
import { noteCollectionsChanged } from '../contexts/CollectionsContext';

// Following an install the API finishes on its own (keepr-api KPR-1655),
// split out of packages.js. packages.js's installPackage and addPackageAddOns
// post through postAndFollow.

// ---------------------------------------------------------------------------
// An install that answers before it is finished (keepr-api KPR-1655).
//
// The API waits 20 s for an install (or "Add an add-on"); one still running
// then answers 202 `{ install, collectionId }` and finishes on its own, and
// GET /api/packages/{key}/install reads the person's run of it. A second
// request while one runs is refused 409 `package_install_running` with that
// run: the person's retry joins it rather than starting another. Either way
// the run is followed here until it is done — resolving to the answer a 201
// would have given, so every caller's success path is the one it had — or
// failed, thrown as the API's own refusal (its `code`, its `message`, its
// `partial`), so a failure part-way is shown as the error it is, never as
// success.
// ---------------------------------------------------------------------------

export const FOLLOW_EVERY_MS = 2000;
// The API reads a run still `running` after 15 minutes as stopped; a little
// past that, this stops asking.
export const FOLLOW_GIVE_UP_MS = 16 * 60 * 1000;
// Checks in a row that may fail (a network blip) before giving up.
export const FOLLOW_MISSES = 5;

const sleep = ms => new Promise((resolve) => { setTimeout(resolve, ms); });

/** The person's latest install (or add-ons) run of a package (keepr-api KPR-1655): `{ key, kind, state, step, collectionId, answer?, error? }`. */
export async function getPackageInstall(key) {
	const { data } = await api.get(`${API_PACKAGES_URL}/${encodeURIComponent(key)}/install`);
	return data?.install || null;
}

/** A run's failure, shaped as the API's refusal so every caller words it as it words one. */
function runError(error, run) {
	const data = error && typeof error === 'object' ? error : {};
	const err = new Error(data.message || 'The package could not be added.');
	err.response = { status: data.statusCode || 500, data };
	err.installRun = run || null;
	return err;
}

/** The follow lost the run: said with where to look, never as success. */
function lostError(run) {
	return runError({
		statusCode: 500,
		code: 'install_lost',
		message: 'keepr could not tell whether adding this package finished. Open the collection to see what it added.',
		...(run?.collectionId ? { collectionId: run.collectionId } : {}),
	}, run);
}

/**
 * Follows a run until it is done (→ its answer) or failed (throws its
 * refusal). `onProgress(run, { joined })` hears each running read. Gives up,
 * with an error that says to open the collection, after FOLLOW_GIVE_UP_MS or
 * FOLLOW_MISSES failed checks in a row, or when the run is gone (404).
 */
export async function followInstall(key, install, { onProgress, joined = false, wait = sleep, now = Date.now } = {}) {
	let run = install;
	let misses = 0;
	const started = now();
	for (;;) {
		if (run?.state === 'done') return run.answer || {};
		if (run?.state === 'failed') throw runError(run.error, run);
		onProgress?.(run, { joined });
		if (now() - started > FOLLOW_GIVE_UP_MS) throw lostError(run);
		await wait(FOLLOW_EVERY_MS); // eslint-disable-line no-await-in-loop
		try {
			run = (await getPackageInstall(key)) || run; // eslint-disable-line no-await-in-loop
			misses = 0;
		} catch (e) {
			if (e?.response?.status === 404) throw lostError(run);
			misses += 1;
			if (misses >= FOLLOW_MISSES) throw lostError(run);
		}
	}
}

/** POSTs an install or add-ons body; follows a 202 or the run a 409 `package_install_running` names. */
export async function postAndFollow(key, url, body, onProgress) {
	let res;
	try {
		res = await api.post(url, body);
	} catch (e) {
		const running = e?.response?.status === 409 && e.response.data?.code === 'package_install_running' ? e.response.data.install : null;
		if (!running) throw e;
		return following(key, running, onProgress, true);
	}
	if (res?.status === 202 && res.data?.install) return following(key, res.data.install, onProgress, false);
	noteCollectionsChanged(); // an install may have made a collection
	return res?.data;
}

async function following(key, run, onProgress, joined) {
	noteCollectionsChanged(); // the collection it fills may be new
	try {
		return await followInstall(key, run, { onProgress, joined });
	} finally {
		noteCollectionsChanged();
	}
}
